import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PrintSettings } from "../types/print";

interface PrintStoreState extends PrintSettings {
  setMode: (mode: PrintSettings["mode"]) => void;
  setPaperWidth: (width: PrintSettings["paperWidth"]) => void;
  setPrinterName: (name: string) => void;
  setReportsPrinterName: (name: string) => void;
  setReceiptDesign: (design: PrintSettings["receiptDesign"]) => void;
  setFont: (font: PrintSettings["font"]) => void;
  setKotAutoPrint: (enabled: boolean) => void;
}

export const usePrintStore = create<PrintStoreState>()(
  persist(
    (set) => ({
      // Default to ESC/POS — most installs are thermal printers and
      // get crisper output that way. Users with a non-ESC/POS printer
      // (or a non-thermal one) can flip to "html" in Settings.
      mode: "escpos",
      paperWidth: 80,
      printerName: "",
      // Empty = use the OS default printer for reports. Distinct from the
      // receipt printer so a store can keep thermal receipts on one device
      // and A4 report output on another.
      reportsPrinterName: "",
      receiptDesign: "classic",
      // Font A is universal — every thermal printer renders it. Font B
      // is denser (more chars/line) but cheap printers silently fall
      // back to A and overrun the row width.
      font: "A",
      // Off by default. Only the terminal next to the kitchen printer
      // should opt in — multiple terminals with this on would all race
      // to print the same KOT.
      kotAutoPrint: false,
      setMode: (mode) => set({ mode }),
      setPaperWidth: (paperWidth) => set({ paperWidth }),
      setPrinterName: (printerName) => set({ printerName }),
      setReportsPrinterName: (reportsPrinterName) =>
        set({ reportsPrinterName }),
      setReceiptDesign: (receiptDesign) => set({ receiptDesign }),
      setFont: (font) => set({ font }),
      setKotAutoPrint: (kotAutoPrint) => set({ kotAutoPrint }),
    }),
    {
      name: "pos-print-settings",
    },
  ),
);
